/**
 * Qual idioma mostrar.
 *
 * A ordem de preferência é a de quem manda mais: a escolha explícita do
 * jogador (guardada no `localStorage`), depois o que o navegador anuncia,
 * depois o padrão do projeto — pt-BR, que é a língua em que ele foi escrito.
 *
 * Tudo aqui é puro ou guardado atrás de `typeof`: este módulo roda em página,
 * em worker (a bateria de determinismo traduz "(sem referência)") e em teste
 * no Bun, onde não há `localStorage` e o `navigator` não sabe de idiomas.
 */

export const IDIOMAS = ['pt-BR', 'en'] as const
export type Idioma = (typeof IDIOMAS)[number]

export const IDIOMA_PADRAO: Idioma = 'pt-BR'

/** Onde a escolha do jogador fica guardada. */
export const CHAVE_DE_ARMAZENAMENTO = 'zinc.idioma'

/**
 * Casa uma etiqueta de idioma (`en-US`, `pt`, `pt-PT`) com um idioma nosso.
 *
 * Só a língua importa, não a região: `pt-PT` recebe pt-BR porque é mais
 * perto que inglês, e `en-GB` recebe `en`. Devolve `null` para o que não
 * conhecemos — quem chama decide o padrão.
 */
export function casarIdioma(etiqueta: string): Idioma | null {
  const lingua = etiqueta.trim().toLowerCase().split(/[-_]/)[0]
  if (lingua === 'pt') return 'pt-BR'
  if (lingua === 'en') return 'en'
  return null
}

export type Ambiente = {
  /** O que estava no `localStorage`, ou `null` se não há nada ou não dá para ler. */
  armazenado?: string | null
  /** `navigator.languages`, na ordem de preferência do navegador. */
  navegador?: readonly string[] | undefined
}

/**
 * Decide o idioma a partir do ambiente, sem tocar em nada global.
 *
 * O valor guardado só vale se for um idioma que EXISTE: uma versão antiga do
 * site pode ter guardado outra coisa, e um valor inválido não pode deixar a
 * página sem idioma.
 */
export function detectarIdioma(ambiente: Ambiente = {}): Idioma {
  const guardado = ambiente.armazenado
  if (guardado && (IDIOMAS as readonly string[]).includes(guardado)) return guardado as Idioma

  for (const etiqueta of ambiente.navegador ?? []) {
    const idioma = casarIdioma(etiqueta)
    if (idioma) return idioma
  }

  return IDIOMA_PADRAO
}

/** Lê a escolha guardada. `null` quando não há, ou quando o storage recusa. */
export function lerArmazenado(): string | null {
  try {
    if (typeof localStorage === 'undefined') return null
    return localStorage.getItem(CHAVE_DE_ARMAZENAMENTO)
  } catch {
    // Modo privado, storage bloqueado, cota estourada: o idioma padrão vale.
    return null
  }
}

function idiomasDoNavegador(): readonly string[] | undefined {
  if (typeof navigator === 'undefined') return undefined
  const n = navigator as Partial<Navigator>
  if (Array.isArray(n.languages) && n.languages.length > 0) return n.languages
  return typeof n.language === 'string' ? [n.language] : undefined
}

let atual: Idioma | null = null

/**
 * O idioma em vigor. Decidido uma vez por carga de página e depois fixo:
 * o texto já desenhado não muda sozinho, então mudar no meio deixaria
 * metade da tela em cada língua.
 */
export function idiomaAtual(): Idioma {
  if (atual === null) {
    atual = detectarIdioma({ armazenado: lerArmazenado(), navegador: idiomasDoNavegador() })
  }
  return atual
}

/**
 * Guarda a escolha do jogador e passa a usá-la.
 *
 * Quem chama (o seletor) recarrega a página em seguida — ver `seletor.ts`
 * para o porquê. Aqui só se guarda; recarregar é decisão da interface.
 */
export function definirIdioma(idioma: Idioma): void {
  atual = idioma
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(CHAVE_DE_ARMAZENAMENTO, idioma)
  } catch {
    // Sem storage a escolha vale só até recarregar. Melhor isso que quebrar.
  }
}

/** Esquece a decisão em memória. Para testes, que trocam o ambiente. */
export function esquecerIdioma(): void {
  atual = null
}

/**
 * A etiqueta para `toLocaleString` e afins.
 *
 * `en` sozinho deixaria o formato de data a critério do motor; `en-US` é o
 * que a maioria dos leitores de inglês espera.
 */
export function localeDeFormatacao(): string {
  return idiomaAtual() === 'en' ? 'en-US' : 'pt-BR'
}
